import { useEffect, useState } from 'react';
import { UseQueryState } from 'urql';

export const nonSuspenseQueryContext = {
  suspense: false
} as const;

interface UseRetainedQueryStateOptions {
  // Indicates that the query is intentionally disabled and retained data should be cleared.
  pause?: boolean;
  // Identifies a boundary where old retained data is no longer safe to show.
  // Use this for scope changes such as switching location, tenant, or entity id.
  resetKey?: unknown;
}

export const useRetainedQueryState = <TData, TVariables = object>(
  queryState: UseQueryState<TData, TVariables>,
  options?: UseRetainedQueryStateOptions
) => {
  const { data, fetching } = queryState;

  // Keep the last successful payload available while the next request is in flight.
  // This lets the current UI refresh in place instead of collapsing back to a
  // broader loading state whenever query variables change.
  const [resolvedData, setResolvedData] = useState<TData | undefined>(data);

  useEffect(() => {
    if (options?.pause) {
      // When the query is intentionally paused, clear retained data so we do not
      // continue showing stale results for an invalid selection.
      setResolvedData(undefined);
      return;
    }

    if (data !== undefined) {
      // Promote the newest successful response to the retained value.
      setResolvedData(data);
    }
  }, [data, options?.pause]);

  useEffect(() => {
    if (options?.resetKey === undefined) {
      return;
    }

    // When the owning key changes, sync retained data to the current query result.
    // If the new query has no data yet, this clears the previous retained payload
    // so results from the old scope are not carried into the new one.
    setResolvedData(data);
  }, [data, options?.resetKey]);

  return {
    resolvedData: data ?? resolvedData,
    isInitialLoading: fetching && resolvedData === undefined,
    isRefreshing: fetching && resolvedData !== undefined
  };
};
